import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { onForegroundMessage, requestPushToken, currentPermission } from '../firebase/messaging';
import { getAuthToken } from '../api/config';
import { navigateTo } from '../utils/navigation';
import {
  getUnreadCount,
  registerDevice,
  getNotifications,
  markNotificationsRead,
  type NotificationItem,
} from '../api/notifications';

interface NotificationsContextValue {
  unread: number;
  refreshUnread: () => void;
  loadNotifications: (limit?: number, offset?: number) => Promise<NotificationItem[]>;
  markRead: (id?: string) => Promise<void>;
}

interface ServiceWorkerNotificationMessage {
  type?: string;
  deepLink?: string;
}

const NotificationsContext = createContext<NotificationsContextValue>({
  unread: 0,
  refreshUnread: () => { /* default noop */ },
  loadNotifications: () => Promise.resolve([]),
  markRead: () => Promise.resolve(),
});

export const useNotifications = () => useContext(NotificationsContext);

// How often to poll the unread badge (light-touch; push covers real-time).
const UNREAD_POLL_MS = 60_000;

export const NotificationsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [unread, setUnread] = useState(0);

  const refreshUnread = useCallback(() => {
    if (!getAuthToken()) return;
    void getUnreadCount()
      .then((r) => { setUnread(r.unread); })
      .catch(() => { /* ignore transient unread failures */ });
  }, []);

  const loadNotifications = useCallback(
    (limit = 30, offset = 0) => getNotifications(limit, offset).catch(() => []),
    []
  );

  const markRead = useCallback(
    async (id?: string) => {
      await markNotificationsRead(id).catch(() => { /* ignore mark-read failures */ });
      refreshUnread();
    },
    [refreshUnread]
  );

  // Register the FCM token for users who already granted permission (returning
  // visitors), so their token stays fresh without re-prompting.
  useEffect(() => {
    if (!getAuthToken()) return;
    if (currentPermission() !== 'granted') return;
    void requestPushToken()
      .then((token) => {
        if (token) void registerDevice(token, 'web').catch(() => { /* non-fatal */ });
      })
      .catch(() => { /* push unavailable */ });
  }, []);

  // Foreground messages: show a notification so focused-tab users still see it.
  useEffect(() => {
    let unsub = () => { /* pending unsubscribe */ };
    void onForegroundMessage((payload) => {
      refreshUnread();
      const n = payload.notification ?? {};
      const data = (payload.data ?? {}) as Record<string, string>;
      if (currentPermission() === 'granted' && 'serviceWorker' in navigator) {
        void navigator.serviceWorker.ready
          .then((reg) =>
            reg.showNotification(n.title ?? 'Kivo', {
              body: n.body ?? '',
              icon: '/favicon/android-chrome-192x192.png',
              data,
              tag: data.deep_link || 'kivo-notification',
            })
          )
          .catch(() => { /* notification display failed */ });
      }
    }).then((fn) => {
      unsub = fn;
    });
    return () => { unsub(); };
  }, [refreshUnread]);

  // Route notification taps forwarded from the service worker.
  useEffect(() => {
    const handler = (event: MessageEvent<ServiceWorkerNotificationMessage>) => {
      const data = event.data;
      if (data.type === 'kivo-notification-click' && data.deepLink) {
        navigateTo(data.deepLink);
        refreshUnread();
      }
    };
    navigator.serviceWorker.addEventListener('message', handler);
    return () => { navigator.serviceWorker.removeEventListener('message', handler); };
  }, [refreshUnread]);

  // Initial + periodic unread refresh.
  useEffect(() => {
    refreshUnread();
    const t = setInterval(refreshUnread, UNREAD_POLL_MS);
    return () => { clearInterval(t); };
  }, [refreshUnread]);

  const value = useMemo(
    () => ({ unread, refreshUnread, loadNotifications, markRead }),
    [unread, refreshUnread, loadNotifications, markRead]
  );

  return <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>;
};
